import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, InputGroup, Row, Col, Table, Alert } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import {
  Calculator,
  Clock,
  Calendar,
  DollarSign,
  Users,
  Plus,
  X,
  Search,
  Info,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Briefcase,
  Heart,
  Gift,
  Minus,
  Star,
  HelpCircle
} from 'lucide-react';
import { COMPREHENSIVE_WAGE_TYPES, getWageTypeConfig, getAbsenceWageTypes } from './wageTypesConfig';
import "react-datepicker/dist/react-datepicker.css";

const SelectWageTypeModal = ({ show, handleClose, handleSelect, wageTypes }) => {
  const [selectedWageTypes, setSelectedWageTypes] = useState([]);
  const [wageTypeQuantities, setWageTypeQuantities] = useState({});
  const [absenceDates, setAbsenceDates] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showPopularOnly, setShowPopularOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [validationErrors, setValidationErrors] = useState([]);
  const [openDatePickerId, setOpenDatePickerId] = useState(null);

  // Enhanced wage types configuration with icons and better UX
  // Use comprehensive wage types configuration
  const ESSENTIAL_WAGE_TYPES = COMPREHENSIVE_WAGE_TYPES;

  // Enhanced filtering with search, category, and popularity filters
  const filteredWageTypes = useMemo(() => {
    let filtered = wageTypes.filter(wageType => ESSENTIAL_WAGE_TYPES.hasOwnProperty(wageType.code));

    console.log('Filtered wage types before applying filters:', filtered);

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(wageType =>
        wageType.code.toLowerCase().includes(query) ||
        wageType.description.toLowerCase().includes(query) ||
        ESSENTIAL_WAGE_TYPES[wageType.code].description.toLowerCase().includes(query)
      );

      console.log('Filtered wage types after search query:', filtered);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(wageType =>
        wageType.code.charAt(0) === selectedCategory
      );

      console.log('Filtered wage types after category filter:', filtered);
    }

    // Filter by popularity
    if (showPopularOnly) {
      filtered = filtered.filter(wageType =>
        ESSENTIAL_WAGE_TYPES[wageType.code].isPopular
      );

      console.log('Filtered wage types after popularity filter:', filtered);
    }

    console.log('Final filtered wage types:', filtered);

    return filtered;
  }, [wageTypes, searchQuery, selectedCategory, showPopularOnly]);

  // Helper function to get input configuration for a wage type
  const getInputConfig = (wageTypeCode) => {
    return ESSENTIAL_WAGE_TYPES[wageTypeCode] || {
      unit: null,
      inputType: 'hours',
      defaultQuantity: 1,
      label: 'Antal',
      requiresAbsenceDates: false,
      icon: Calculator,
      color: '#6b7280',
      description: 'Standard löneart'
    };
  };

  // Quick action handlers
  const handleQuickAction = (wageTypeId, value) => {
    setWageTypeQuantities(prev => ({
      ...prev,
      [wageTypeId]: value
    }));
  };

  // Validation function
  const validateSelection = () => {
    const errors = [];

    selectedWageTypes.forEach(wageType => {
      const config = getInputConfig(wageType.code);
      const quantity = wageTypeQuantities[wageType.id] || config.defaultQuantity;

      // Check if quantity is valid
      if (quantity <= 0) {
        errors.push(`${wageType.code}: Antal måste vara större än 0`);
      }

      // Check absence dates for wage types that require them
      if (config.requiresAbsenceDates) {
        const dates = absenceDates[wageType.id];
        if (!dates || !dates.startDate) {
          errors.push(`${wageType.code} (${config.absenceType}) kräver frånvarodatum enligt svensk lag`);
        }
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Handle absence date changes
  const handleAbsenceDateChange = (wageTypeId, date) => {
    setAbsenceDates(prev => ({
      ...prev,
      [wageTypeId]: {
        startDate: date
      }
    }));
  };

  // Format wage types for react-select
  const selectOptions = useMemo(() => {
    return filteredWageTypes.map(wageType => ({
      value: wageType.id,
      label: `${wageType.code} - ${wageType.description}`,
      wageType: wageType
    }));
  }, [filteredWageTypes]);

  // Group options by category (first digit of code usually indicates category)
  const groupedOptions = useMemo(() => {
    const groups = {};

    selectOptions.forEach(option => {
      const category = option.wageType.code.charAt(0);
      const categoryName = getCategoryName(category);

      if (!groups[categoryName]) {
        groups[categoryName] = [];
      }

      groups[categoryName].push(option);
    });

    return Object.keys(groups).map(key => ({
      label: key,
      options: groups[key]
    }));
  }, [selectOptions]);

  // Helper to convert category code to readable name
  function getCategoryName(categoryCode) {
    const categories = {
      '1': 'Lön och ersättning',
      '2': 'Tillägg',
      '3': 'Frånvaro',
      '4': 'Förmåner',
      '5': 'Avdrag',
      '9': 'Övrigt'
    };

    return categories[categoryCode] || `Kategori ${categoryCode}`;
  }

  const handleSelectChange = (selected) => {
    const newSelectedWageTypes = selected ? selected.map(option => option.wageType) : [];
    setSelectedWageTypes(newSelectedWageTypes);

    // Initialize quantities for newly selected wage types
    const newQuantities = { ...wageTypeQuantities };
    const newAbsenceDates = { ...absenceDates };

    newSelectedWageTypes.forEach(wageType => {
      if (!newQuantities[wageType.id]) {
        const config = getInputConfig(wageType.code);
        newQuantities[wageType.id] = config.defaultQuantity;
      }

      // Initialize absence dates for wage types that require them
      const config = getInputConfig(wageType.code);
      if (config.requiresAbsenceDates && !newAbsenceDates[wageType.id]) {
        newAbsenceDates[wageType.id] = {
          startDate: null
        };
      }
    });

    // Remove quantities and absence dates for unselected wage types
    Object.keys(newQuantities).forEach(wageTypeId => {
      if (!newSelectedWageTypes.find(wt => wt.id === wageTypeId)) {
        delete newQuantities[wageTypeId];
        delete newAbsenceDates[wageTypeId];
      }
    });

    setWageTypeQuantities(newQuantities);
    setAbsenceDates(newAbsenceDates);
  };

  const handleQuantityChange = (wageTypeId, value) => {
    setWageTypeQuantities(prev => ({
      ...prev,
      [wageTypeId]: parseFloat(value) || 0
    }));
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '38px',
      borderColor: '#ced4da',
      boxShadow: 'none'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'var(--bs-primary)' : state.isFocused ? '#f8f9fa' : null,
      color: state.isSelected ? 'white' : 'black',
      '&:active': {
        backgroundColor: 'var(--bs-primary)'
      }
    })
  };

  const customFilterOption = (option, rawInput) => {
    const input = rawInput.toLowerCase().trim();
    if (!input) return true;

    const { label, wageType } = option.data;

    return (
      label.toLowerCase().includes(input) ||
      wageType.code.toLowerCase().includes(input) ||
      wageType.description.toLowerCase().includes(input)
    );
  };

  return (
    <>
      <style>{`
        .modern-wage-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modern-wage-modal .modal-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-bottom: 1px solid #e5e7eb;
          border-radius: 16px 16px 0 0;
          padding: 2rem 2rem 1.5rem 2rem;
        }

        .modern-wage-modal .modal-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modern-wage-modal .modal-body {
          padding: 2rem;
          background: #f8fafc;
          max-height: 70vh;
          overflow-y: auto;
        }

        .modern-wage-modal .modal-footer {
          background: white;
          border-top: 1px solid #e5e7eb;
          border-radius: 0 0 16px 16px;
          padding: 1.5rem 2rem;
          display: flex;
          gap: 1rem;
          justify-content: space-between;
          align-items: center;
        }

        .info-section {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          border: 1px solid #86efac;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          display: flex;
          gap: 1rem;
        }

        .info-icon {
          color: var(--bs-primary);
          flex-shrink: 0;
        }

        .info-text {
          color: #166534;
          font-size: 0.875rem;
          line-height: 1.6;
        }

        .filters-section {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .search-container {
          position: relative;
          margin-bottom: 1rem;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2.5rem;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #374151;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .filter-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 0.5rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          color: #6b7280;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-btn:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        .filter-btn.active {
          background: var(--bs-primary);
          color: white;
          border-color: var(--bs-primary);
        }

        .wage-types-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 0.75rem;
          margin-bottom: 2rem;
        }

        .wage-type-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 0.75rem;
          transition: all 0.2s ease;
          cursor: pointer;
          position: relative;
          min-height: auto;
          overflow: visible;
          z-index: 1;
        }

        .wage-type-card:hover {
          border-color: var(--bs-primary);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .wage-type-card.selected {
          border-color: var(--bs-primary);
          background: #f0fdf4;
          box-shadow: 0 0 0 1px var(--bs-primary);
        }

        .wage-type-card.has-open-datepicker {
          z-index: 10001 !important;
        }

        .wage-type-icon {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          background-color: #f3f4f6;
          flex-shrink: 0;
          margin-right: 0.75rem;
        }

        .wage-type-info {
          flex: 1;
          min-width: 0;
          overflow: visible;
          position: relative;
        }

        .wage-type-info h6 {
          margin: 0;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #1e293b;
          line-height: 1.3;
          word-wrap: break-word;
          overflow-wrap: break-word;
          padding-right: 1.5rem; /* Make space for info icon */
        }

        .wage-type-code {
          font-size: 0.6875rem;
          color: #6b7280;
          font-weight: 500;
          margin-top: 0.25rem;
        }

        .info-icon {
          position: absolute;
          top: 0;
          right: 0;
          color: #9ca3af;
          cursor: help;
          font-size: 0.6875rem;
          z-index: 10;
        }

        .tooltip {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #1f2937;
          color: white;
          padding: 0.5rem 0.75rem;
          border-radius: 4px;
          font-size: 0.6875rem;
          white-space: nowrap;
          z-index: 10000;
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          pointer-events: none;
          margin-bottom: 0.5rem;
          max-width: 200px;
          white-space: normal;
          word-wrap: break-word;
        }

        .tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 4px solid transparent;
          border-top-color: #1f2937;
        }

        .info-icon:hover .tooltip {
          opacity: 1;
          visibility: visible;
        }


        .quantity-section {
          margin-bottom: 1rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e5e7eb;
        }

        .quantity-label {
          font-size: 0.6875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .quantity-input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 0.6875rem;
        }

        .quick-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-top: 0.5rem;
        }

        .quick-action-btn {
          padding: 0.25rem 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          color: #6b7280;
          font-size: 0.6875rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .quick-action-btn:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .absence-dates-section {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .absence-dates-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.5rem;
        }

        .absence-date-input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 0.6875rem;
        }

        .react-datepicker-popper {
          z-index: 10002 !important;
        }

        .react-datepicker {
          background: white !important;
          border: 1px solid #d1d5db !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .react-datepicker__header {
          background: white !important;
        }

        .react-datepicker__month {
          background: white !important;
        }

        .absence-warning {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 0.5rem;
          border-radius: 4px;
          font-size: 0.6875rem;
          margin-top: 0.5rem;
        }

        .selected-summary {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          border: 1px solid #86efac;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .summary-title {
          font-weight: 600;
          color: #166534;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .summary-count {
          background: var(--bs-primary);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 16px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .selected-items {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .selected-item {
          background: white;
          border: 1px solid #86efac;
          border-radius: 6px;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          color: #166534;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .remove-btn {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 0;
          display: flex;
          align-items: center;
        }

        .remove-btn:hover {
          color: #dc2626;
        }

        .validation-errors {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .error-item {
          color: #dc2626;
          font-size: 0.875rem;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .error-item:last-child {
          margin-bottom: 0;
        }

        .modern-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 120px;
          justify-content: center;
        }

        .btn-secondary-modern {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }

        .btn-secondary-modern:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .btn-primary-modern {
          background: var(--bs-primary);
          color: white;
        }

        .btn-primary-modern:hover:not(:disabled) {
          background: var(--primary-hover);
          transform: translateY(-1px);
        }

        .btn-primary-modern:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        @media (max-width: 768px) {
          .modern-wage-modal .modal-header,
          .modern-wage-modal .modal-body,
          .modern-wage-modal .modal-footer {
            padding: 1.5rem 1rem;
          }
          
          .wage-types-grid {
            grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
            gap: 0.5rem;
          }
          
          .wage-type-card {
            padding: 0.5rem;
          }
          
          .wage-type-icon {
            width: 20px;
            height: 20px;
          }
          
          .wage-type-info h6 {
            font-size: 0.75rem;
          }
          
          .filter-buttons {
            flex-direction: column;
          }
          
          .modern-wage-modal .modal-footer {
            flex-direction: column;
          }
          
          .modern-btn {
            width: 100%;
          }
        }
      `}</style>

      <Modal show={show} onHide={handleClose} size="lg" className="modern-wage-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <Calculator size={24} color="var(--bs-primary)" />
            Välj löneartiklar
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Info Section
          <div className="info-section">
            <Info size={20} className="info-icon" />
            <div className="info-text">
              <strong>Viktigt:</strong> Enligt svensk lag måste frånvarodatum anges för VAB och föräldrapenning när dessa rapporteras till Skatteverket.
              Välj löneartiklar som passar din anställds situation.
            </div>
          </div>*/}

          {/* Filters Section */}
          <div className="filters-section">
            <div className="search-container">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Sök efter löneart, beskrivning eller kategori..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="filter-buttons">
              <button
                className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                <TrendingUp size={16} />
                Alla ({filteredWageTypes.length})
              </button>
              <button
                className={`filter-btn ${showPopularOnly ? 'active' : ''}`}
                onClick={() => setShowPopularOnly(!showPopularOnly)}
              >
                <Star size={16} />
                Populära
              </button>
              <button
                className={`filter-btn ${selectedCategory === '1' ? 'active' : ''}`}
                onClick={() => setSelectedCategory(selectedCategory === '1' ? 'all' : '1')}
              >
                <DollarSign size={16} />
                Lön & ersättning
              </button>
              <button
                className={`filter-btn ${selectedCategory === '3' ? 'active' : ''}`}
                onClick={() => setSelectedCategory(selectedCategory === '3' ? 'all' : '3')}
              >
                <Heart size={16} />
                Frånvaro
              </button>
            </div>
          </div>

          {/* Wage Types Grid */}
          <div className="wage-types-grid">
            {filteredWageTypes.map((wageType) => {
              const config = getInputConfig(wageType.code);
              const IconComponent = config.icon;
              const isSelected = selectedWageTypes.some(wt => wt.id === wageType.id);
              const currentQuantity = wageTypeQuantities[wageType.id] || config.defaultQuantity;
              const currentAbsenceDates = absenceDates[wageType.id] || { startDate: null };

              const hasOpenDatePicker = openDatePickerId === wageType.id;

              return (
                <div
                  key={wageType.id}
                  className={`wage-type-card ${isSelected ? 'selected' : ''} ${hasOpenDatePicker ? 'has-open-datepicker' : ''}`}
                  onClick={() => {
                    if (isSelected) {
                      // Remove from selection
                      const newSelected = selectedWageTypes.filter(wt => wt.id !== wageType.id);
                      setSelectedWageTypes(newSelected);

                      // Clean up quantities and dates
                      const newQuantities = { ...wageTypeQuantities };
                      const newAbsenceDates = { ...absenceDates };
                      delete newQuantities[wageType.id];
                      delete newAbsenceDates[wageType.id];
                      setWageTypeQuantities(newQuantities);
                      setAbsenceDates(newAbsenceDates);
                    } else {
                      // Add to selection
                      setSelectedWageTypes([...selectedWageTypes, wageType]);

                      // Initialize quantity and dates
                      const newQuantities = { ...wageTypeQuantities };
                      const newAbsenceDates = { ...absenceDates };
                      newQuantities[wageType.id] = config.defaultQuantity;

                      if (config.requiresAbsenceDates) {
                        newAbsenceDates[wageType.id] = {
                          startDate: null
                        };
                      }

                      setWageTypeQuantities(newQuantities);
                      setAbsenceDates(newAbsenceDates);
                    }
                  }}
                >

                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div className="wage-type-icon">
                      <IconComponent size={18} />
                    </div>
                    <div className="wage-type-info">
                      <h6>{wageType.description}</h6>
                      <div className="wage-type-code">{wageType.code}</div>
                      <div className="info-icon">
                        <HelpCircle size={12} />
                        <div className="tooltip">
                          {config.description}
                        </div>
                      </div>
                    </div>
                  </div>

                  {isSelected && (
                    <>
                      <div className="quantity-section" onClick={(e) => e.stopPropagation()}>
                        <div className="quantity-label">
                          Antal {config.label.toLowerCase()}
                        </div>
                        <input
                          type="number"
                          className="quantity-input"
                          min="0"
                          step={config.inputType === 'amount' ? '0.01' : '1'}
                          value={currentQuantity}
                          onChange={(e) => handleQuantityChange(wageType.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                        />

                        {config.quickActions && (
                          <div className="quick-actions">
                            {config.quickActions.map((value) => (
                              <button
                                key={value}
                                className="quick-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuickAction(wageType.id, value);
                                }}
                              >
                                {value}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {config.requiresAbsenceDates && (
                        <div className="absence-dates-section" onClick={(e) => e.stopPropagation()}>
                          <div className="quantity-label">
                            Frånvarodatum (krävs enligt svensk lag)
                          </div>
                          <div className="absence-dates-grid">
                            <div onClick={(e) => e.stopPropagation()}>
                              <DatePicker
                                selected={currentAbsenceDates.startDate}
                                onChange={(date) => handleAbsenceDateChange(wageType.id, date)}
                                onCalendarOpen={() => setOpenDatePickerId(wageType.id)}
                                onCalendarClose={() => setOpenDatePickerId(null)}
                                className="absence-date-input"
                                dateFormat="yyyy-MM-dd"
                                placeholderText="Välj datum"
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          {!currentAbsenceDates.startDate && (
                            <div className="absence-warning">
                              <AlertTriangle size={12} />
                              Frånvarodatum krävs för {config.absenceType}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected Summary */}
          {selectedWageTypes.length > 0 && (
            <div className="selected-summary">
              <div className="summary-header">
                <div className="summary-title">
                  <CheckCircle2 size={16} />
                  Valda löneartiklar
                </div>
                <div className="summary-count">
                  {selectedWageTypes.length} valda
                </div>
              </div>
              <div className="selected-items">
                {selectedWageTypes.map((wageType) => {
                  const config = getInputConfig(wageType.code);
                  return (
                    <div key={wageType.id} className="selected-item">
                      <span>{wageType.code} - {wageType.description}</span>
                      <button
                        className="remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newSelected = selectedWageTypes.filter(wt => wt.id !== wageType.id);
                          setSelectedWageTypes(newSelected);

                          const newQuantities = { ...wageTypeQuantities };
                          const newAbsenceDates = { ...absenceDates };
                          delete newQuantities[wageType.id];
                          delete newAbsenceDates[wageType.id];
                          setWageTypeQuantities(newQuantities);
                          setAbsenceDates(newAbsenceDates);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="validation-errors">
              {validationErrors.map((error, index) => (
                <div key={index} className="error-item">
                  <AlertTriangle size={14} />
                  {error}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <small className="text-muted">
              💡 Du kan alltid lägga till fler löneartiklar senare
            </small>
            <div className="d-flex gap-2">
              <button className="modern-btn btn-secondary-modern" onClick={handleClose}>
                <X size={16} />
                Avbryt
              </button>
              <button
                className="modern-btn btn-primary-modern"
                onClick={() => {
                  if (!validateSelection()) {
                    return;
                  }

                  // Helper function to format date in local timezone (avoid UTC conversion)
                  const formatDateLocal = (date) => {
                    if (!date) return null;
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };

                  // Prepare wage types with quantities, input types, and absence dates
                  const wageTypesWithData = selectedWageTypes.map(wageType => {
                    const config = getInputConfig(wageType.code);
                    const quantity = wageTypeQuantities[wageType.id] || config.defaultQuantity;
                    const dates = absenceDates[wageType.id];

                    const result = {
                      wage_type_id: wageType.id, // Backend expects wage_type_id
                      code: wageType.code,
                      description: wageType.description,
                      quantity: quantity,
                      inputType: config.inputType
                    };

                    // Add absence dates if required (set both start and end to the same date)
                    if (config.requiresAbsenceDates && dates && dates.startDate) {
                      const formattedDate = formatDateLocal(dates.startDate);
                      result.absenceStartDate = formattedDate;
                      result.absenceEndDate = formattedDate; // Same as start date
                    }

                    return result;
                  });

                  handleSelect(wageTypesWithData);
                  setSelectedWageTypes([]);
                  setWageTypeQuantities({});
                  setAbsenceDates({});
                  setValidationErrors([]);
                }}
                disabled={selectedWageTypes.length === 0}
              >
                <Plus size={16} />
                Lägg till ({selectedWageTypes.length})
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SelectWageTypeModal;